import {
    collection,
    doc,
    setDoc,
    addDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    limit
} from "firebase/firestore";
import { db } from "../config";

// --- Featured Event ---

export const getFeaturedEvent = async () => {
    try {
        const tournamentsRef = collection(db, "tournaments");
        const q = query(tournamentsRef, where("isFeatured", "==", true), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const featuredDoc = querySnapshot.docs[0];
            return { id: featuredDoc.id, ...featuredDoc.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Błąd podczas pobierania featured eventu:", error);
        throw error;
    }
};

export const updateFeaturedEvent = async (featuredEventData) => {
    try {
        const existingEvent = await getFeaturedEvent();

        if (existingEvent) {
            const tournamentRef = doc(db, "tournaments", existingEvent.id);
            const featuredEventWithTimestamp = {
                ...featuredEventData,
                isFeatured: true,
                modifiedAt: new Date().toISOString()
            };
            await updateDoc(tournamentRef, featuredEventWithTimestamp);
            return existingEvent.id;
        } else {
            const tournamentsRef = collection(db, "tournaments");
            const featuredEventWithTimestamp = {
                ...featuredEventData,
                isFeatured: true,
                createdAt: new Date().toISOString(),
                modifiedAt: new Date().toISOString()
            };
            const createdDocRef = await addDoc(tournamentsRef, featuredEventWithTimestamp);
            return createdDocRef.id;
        }
    } catch (error) {
        console.error("Błąd podczas aktualizacji featured eventu:", error);
        throw error;
    }
};

// --- Organizatorzy ---

export const addOrganizer = async (organizerId, organizerData) => {
    try {
        const organizerRef = doc(db, "organizers", organizerId);
        const organizerWithTimestamps = {
            ...organizerData,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };
        await setDoc(organizerRef, organizerWithTimestamps);
        return organizerId;
    } catch (error) {
        console.error("Błąd podczas dodawania organizatora:", error);
        throw error;
    }
};

export const createOrganizer = async (organizerData) => {
    try {
        const organizersRef = collection(db, "organizers");
        const organizerWithTimestamps = {
            ...organizerData,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };
        const createdDocRef = await addDoc(organizersRef, organizerWithTimestamps);
        return createdDocRef.id;
    } catch (error) {
        console.error("Błąd podczas tworzenia organizatora:", error);
        throw error;
    }
};

export const getOrganizer = async (organizerId) => {
    try {
        const organizerRef = doc(db, "organizers", organizerId);
        const organizerDoc = await getDoc(organizerRef);

        if (organizerDoc.exists()) {
            return { id: organizerDoc.id, ...organizerDoc.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Błąd podczas pobierania organizatora:", error);
        throw error;
    }
};

export const getAllOrganizers = async () => {
    try {
        const organizersRef = collection(db, "organizers");
        const querySnapshot = await getDocs(organizersRef);

        return querySnapshot.docs.map(organizerDoc => ({
            id: organizerDoc.id,
            ...organizerDoc.data()
        }));
    } catch (error) {
        console.error("Błąd podczas pobierania organizatorów:", error);
        throw error;
    }
};

export const updateOrganizer = async (organizerId, organizerData) => {
    try {
        const organizerRef = doc(db, "organizers", organizerId);
        const organizerWithTimestamp = {
            ...organizerData,
            modifiedAt: new Date().toISOString()
        };
        await updateDoc(organizerRef, organizerWithTimestamp);
        return organizerId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji organizatora:", error);
        throw error;
    }
};

export const deleteOrganizer = async (organizerId) => {
    try {
        const organizerRef = doc(db, "organizers", organizerId);
        await deleteDoc(organizerRef);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania organizatora:", error);
        throw error;
    }
};

// --- Turnieje ---

export const addTournament = async (tournamentId, tournamentData) => {
    try {
        const tournamentRef = doc(db, "tournaments", tournamentId);
        const tournamentWithTimestamps = {
            ...tournamentData,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };
        await setDoc(tournamentRef, tournamentWithTimestamps);
        return tournamentId;
    } catch (error) {
        console.error("Błąd podczas dodawania turnieju:", error);
        throw error;
    }
};

export const createTournament = async (tournamentData) => {
    try {
        const tournamentWithLocation = {
            ...tournamentData,
            customLocation: tournamentData.customLocation || null,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };

        delete tournamentWithLocation.organizer;

        const tournamentsRef = collection(db, "tournaments");
        const createdDocRef = await addDoc(tournamentsRef, tournamentWithLocation);
        return createdDocRef.id;
    } catch (error) {
        console.error("Błąd podczas tworzenia turnieju:", error);
        throw error;
    }
};

export const getTournament = async (tournamentId) => {
    try {
        const tournamentRef = doc(db, "tournaments", tournamentId);
        const tournamentDoc = await getDoc(tournamentRef);

        if (tournamentDoc.exists()) {
            return { id: tournamentDoc.id, ...tournamentDoc.data() };
        } else {
            return null;
        }
    } catch (error) {
        console.error("Błąd podczas pobierania turnieju:", error);
        throw error;
    }
};

export const getAllTournaments = async () => {
    try {
        const tournamentsRef = collection(db, "tournaments");
        const querySnapshot = await getDocs(tournamentsRef);

        return querySnapshot.docs.map(tournamentDoc => ({
            id: tournamentDoc.id,
            ...tournamentDoc.data()
        }));
    } catch (error) {
        console.error("Błąd podczas pobierania turniejów:", error);
        throw error;
    }
};

export const getTournamentsWithOrganizers = async () => {
    try {
        const tournaments = await getAllTournaments();

        const tournamentsWithOrganizers = await Promise.all(
            tournaments.map(async (tournament) => {
                if (tournament.organizer_id) {
                    const organizer = await getOrganizer(tournament.organizer_id);
                    return {
                        ...tournament,
                        organizer,
                        customLocation: tournament.customLocation || null
                    };
                }
                return {
                    ...tournament,
                    customLocation: tournament.customLocation || null
                };
            })
        );

        return tournamentsWithOrganizers;
    } catch (error) {
        console.error("Błąd podczas pobierania turniejów z organizatorami:", error);
        throw error;
    }
};

export const getTournamentsByRank = async (rank) => {
    try {
        const tournamentsRef = collection(db, "tournaments");
        const q = query(tournamentsRef, where("rank", "==", rank));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(tournamentDoc => ({
            id: tournamentDoc.id,
            ...tournamentDoc.data()
        }));
    } catch (error) {
        console.error("Błąd podczas pobierania turniejów:", error);
        throw error;
    }
};

export const updateTournament = async (tournamentId, tournamentData) => {
    try {
        const tournamentWithLocation = {
            ...tournamentData,
            customLocation: tournamentData.customLocation || null,
            modifiedAt: new Date().toISOString()
        };

        delete tournamentWithLocation.organizer;

        const tournamentRef = doc(db, "tournaments", tournamentId);
        await updateDoc(tournamentRef, tournamentWithLocation);
        return tournamentId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji turnieju:", error);
        throw error;
    }
};

export const deleteTournament = async (tournamentId) => {
    try {
        const tournamentRef = doc(db, "tournaments", tournamentId);
        await deleteDoc(tournamentRef);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania turnieju:", error);
        throw error;
    }
};

// --- Szablony Turniejów ---

export const getAllTemplates = async () => {
    try {
        const templatesRef = collection(db, "tournament_templates");
        const querySnapshot = await getDocs(templatesRef);
        return querySnapshot.docs.map(templateDoc => ({
            id: templateDoc.id,
            ...templateDoc.data()
        }));
    } catch (error) {
        console.error("Błąd podczas pobierania szablonów:", error);
        throw error;
    }
};

export const saveTemplate = async (templateData) => {
    try {
        const templatesRef = collection(db, "tournament_templates");
        const templateWithTimestamp = {
            ...templateData,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };
        const createdDocRef = await addDoc(templatesRef, templateWithTimestamp);
        return createdDocRef.id;
    } catch (error) {
        console.error("Błąd podczas zapisywania szablonu:", error);
        throw error;
    }
};

export const deleteTemplate = async (templateId) => {
    try {
        const templateRef = doc(db, "tournament_templates", templateId);
        await deleteDoc(templateRef);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania szablonu:", error);
        throw error;
    }
};

// --- Turnieje Regularne ---

export const createRegularTournament = async (regularTournamentData) => {
    try {
        console.log('Test połączenia z Firestore - sprawdzanie dostępu do kolekcji tournaments');

        const testTournamentsRef = collection(db, "tournaments");
        const testSnapshot = await getDocs(testTournamentsRef);
        console.log('Dostęp do kolekcji tournaments OK, liczba dokumentów:', testSnapshot.size);

        console.log('Zapisywanie turnieju regularnego w kolekcji tournaments z flagą isRegular');
        console.log('Dzień tygodnia z danych:', regularTournamentData.dayOfWeek);

        const regularTournamentWithTimestamp = {
            ...regularTournamentData,
            isRegular: true,
            createdAt: new Date().toISOString(),
            modifiedAt: new Date().toISOString()
        };

        console.log('Dane turnieju regularnego do zapisu:', regularTournamentWithTimestamp);

        const tournamentsRef = collection(db, "tournaments");
        const createdDocRef = await addDoc(tournamentsRef, regularTournamentWithTimestamp);

        console.log('Turniej regularny zapisany z ID:', createdDocRef.id);
        return createdDocRef.id;
    } catch (error) {
        console.error("Szczegółowy błąd podczas tworzenia turnieju regularnego:", error);
        console.error("Kod błędu:", error.code);
        console.error("Wiadomość błędu:", error.message);
        console.error("Stack trace:", error.stack);
        throw error;
    }
};

export const getAllRegularTournaments = async () => {
    try {
        const tournamentsRef = collection(db, "tournaments");
        const q = query(tournamentsRef, where("isRegular", "==", true));
        const querySnapshot = await getDocs(q);

        const regularTournaments = querySnapshot.docs.map(tournamentDoc => ({
            id: tournamentDoc.id,
            ...tournamentDoc.data()
        }));

        console.log('Pobrane turnieje regularne z bazy:', regularTournaments);
        regularTournaments.forEach(tournament => {
            console.log(`Turniej: ${tournament.name}, dzień tygodnia: "${tournament.dayOfWeek}"`);
        });

        return regularTournaments;
    } catch (error) {
        console.error("Błąd podczas pobierania turniejów regularnych:", error);
        throw error;
    }
};

export const getRegularTournamentsWithOrganizers = async () => {
    try {
        const regularTournaments = await getAllRegularTournaments();

        const regularTournamentsWithOrganizers = await Promise.all(
            regularTournaments.map(async (tournament) => {
                if (tournament.organizer_id) {
                    const organizer = await getOrganizer(tournament.organizer_id);
                    return {
                        ...tournament,
                        organizer,
                        customLocation: tournament.customLocation || null
                    };
                }
                return {
                    ...tournament,
                    customLocation: tournament.customLocation || null
                };
            })
        );

        return regularTournamentsWithOrganizers;
    } catch (error) {
        console.error("Błąd podczas pobierania turniejów regularnych z organizatorami:", error);
        throw error;
    }
};

export const updateRegularTournament = async (regularTournamentId, regularTournamentData) => {
    try {
        const regularTournamentWithTimestamp = {
            ...regularTournamentData,
            isRegular: true,
            modifiedAt: new Date().toISOString()
        };

        const tournamentRef = doc(db, "tournaments", regularTournamentId);
        await updateDoc(tournamentRef, regularTournamentWithTimestamp);
        return regularTournamentId;
    } catch (error) {
        console.error("Błąd podczas aktualizacji turnieju regularnego:", error);
        throw error;
    }
};

export const deleteRegularTournament = async (regularTournamentId) => {
    try {
        const tournamentRef = doc(db, "tournaments", regularTournamentId);
        await deleteDoc(tournamentRef);
        return true;
    } catch (error) {
        console.error("Błąd podczas usuwania turnieju regularnego:", error);
        throw error;
    }
};

export const generateRegularTournamentEvents = (regularTournaments, year, month) => {
    const events = [];
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    regularTournaments.forEach(regularTournament => {
        const { dayOfWeek, time, name, city, price, url, meleeLink, format, structure, rank, organizer_id, organizer, prizes, customLocation, duration = 1 } = regularTournament;

        const dayMap = {
            'poniedziałek': 2, 'wtorek': 3, 'środa': 4, 'czwartek': 5, 'piątek': 6, 'sobota': 7, 'niedziela': 1,
            'monday': 2, 'tuesday': 3, 'wednesday': 4, 'thursday': 5, 'friday': 6, 'saturday': 7, 'sunday': 1
        };

        const targetDayOfWeek = dayMap[dayOfWeek.toLowerCase()];

        if (targetDayOfWeek === undefined) {
            console.warn(`Nieznany dzień tygodnia: ${dayOfWeek}`);
            return;
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const jsDayOfWeek = date.getDay();
            const dayOfWeekFromDate = jsDayOfWeek;

            if (dayOfWeekFromDate === targetDayOfWeek) {
                events.push({
                    id: `regular_${regularTournament.id}_${date.toISOString().split('T')[0]}`,
                    name,
                    date: date.toISOString().split('T')[0],
                    time,
                    city,
                    price,
                    url,
                    meleeLink,
                    format,
                    structure,
                    rank,
                    organizer_id,
                    organizer,
                    prizes,
                    customLocation,
                    duration,
                    isRegular: true,
                    regularTournamentId: regularTournament.id,
                    dayOfWeek
                });
            }
        }

        const lastDayOfMonth = new Date(year, month, daysInMonth);
        const lastDayOfWeek = lastDayOfMonth.getDay();

        if (lastDayOfWeek !== targetDayOfWeek) {
            for (let day = 1; day <= 7; day++) {
                const nextMonthDate = new Date(year, month + 1, day);
                const nextMonthDayOfWeek = nextMonthDate.getDay();

                if (nextMonthDayOfWeek === targetDayOfWeek) {
                    events.push({
                        id: `regular_${regularTournament.id}_${nextMonthDate.toISOString().split('T')[0]}`,
                        name,
                        date: nextMonthDate.toISOString().split('T')[0],
                        time,
                        city,
                        price,
                        url,
                        meleeLink,
                        format,
                        structure,
                        rank,
                        organizer_id,
                        organizer,
                        prizes,
                        customLocation,
                        duration,
                        isRegular: true,
                        regularTournamentId: regularTournament.id,
                        dayOfWeek
                    });
                    break;
                }
            }
        }
    });

    return events;
};
